/**
 * BillingCodesSection
 *
 * CPT codes, ICD-10 codes, suggested CPT codes, and psychotherapy minutes.
 */

import React from 'react';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker';
import { Stack, TextField } from '@mui/material';
import type { WorkingClaimLineItem } from '@store/generated/encounters';

import type { EncounterAttestations } from '../../types/attestations';

interface BillingCodesSectionProps {
  cptCodes: string[];
  onCptCodesChange: (codes: string[]) => void;
  cptLineItems?: WorkingClaimLineItem[];
  onCptLineItemsChange?: (items: WorkingClaimLineItem[]) => void;
  suggestedCptCodes?: string[];
  onUseSuggestedCptCodes?: () => void;

  icd10Codes: string[];
  onIcd10CodesChange: (codes: string[]) => void;

  // Psychotherapy minutes (shown when 2 CPT codes selected)
  psychotherapyMinutes?: number | null;
  onPsychotherapyMinutesChange?: (
    field: keyof EncounterAttestations,
    value: number | null,
  ) => void;

  editable?: boolean;
}

export const BillingCodesSection: React.FC<BillingCodesSectionProps> = ({
  cptCodes,
  onCptCodesChange,
  cptLineItems,
  onCptLineItemsChange,
  suggestedCptCodes,
  onUseSuggestedCptCodes,
  icd10Codes,
  onIcd10CodesChange,
  psychotherapyMinutes,
  onPsychotherapyMinutesChange,
  editable = true,
}) => {
  const showPsychotherapyMinutes = cptCodes.length === 2;

  return (
    <Stack spacing={2}>
      {/* CPT Codes */}
      <CPTCodePicker
        value={cptCodes}
        onChange={onCptCodesChange}
        onLineItemsChange={onCptLineItemsChange}
        lineItems={cptLineItems}
        suggestedCodes={suggestedCptCodes}
        onUseSuggestedCodes={onUseSuggestedCptCodes}
        showHeader={false}
        disabled={!editable}
      />

      {/* ICD-10 Codes */}
      <ICD10CodePicker
        value={icd10Codes}
        onChange={onIcd10CodesChange}
        showHeader={false}
        size="small"
        disabled={!editable}
      />

      {/* Psychotherapy Minutes — only when 2 CPT codes (E&M + psychotherapy) */}
      {showPsychotherapyMinutes && (
        <TextField
          type="number"
          label="Psychotherapy Minutes"
          value={psychotherapyMinutes ?? ''}
          onChange={e => {
            const val = e.target.value
              ? parseInt(e.target.value, 10)
              : null;
            onPsychotherapyMinutesChange?.('psychotherapy_minutes', val);
          }}
          fullWidth
          size="small"
          disabled={!editable}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 0 }}
        />
      )}
    </Stack>
  );
};
